import React, { useEffect } from 'react';
import { Table, TableWrapper, Cell } from 'react-native-table-component';
import { View } from 'react-native';
import * as UserActions from '../../redux/actions/userAction';
import * as JobActions from '../../redux/actions/jobAction';
import csv from 'csvtojson';
import { Text, Input, useToast } from 'native-base';
import { Button } from 'native-base';
import { useDispatch, useSelector } from "react-redux";
import { textStyle } from 'src/styles/textStyle';
import { tableStyle } from 'src/styles/tableStyle';
import { isElectron } from 'src/utils/miscUtils';
import { getText } from 'src/utils/i18n';
import { localizeResultLabel } from 'src/utils/resultLocalization';
import { filterMeasurementRowsByRejectSnapshot, filterRowsByRejectSnapshot } from 'src/utils/rejectConfig';
import { useResponsive } from 'src/hooks/useResponsive';
import { getDefaultParticleDistributionSettings } from 'src/utils/particleDistributionSettings';

const TABLE_HEAD = ['Name', 'Lower Bound', 'Upper Bound', 'Count', '% by count', '% by weight'];

function getSettingsKey({ orgId, grainId, profileId, settingName }) {
  return `${orgId || ''}:${grainId || ''}:${profileId || ''}:${settingName || ''}`;
}

function rowsFromSettings(settings, settingName) {
  const source = Array.isArray(settings) && settings.length ? settings : getDefaultParticleDistributionSettings(settingName);
  return source.map((row, index) => [
    row?.[2] || `Range ${index + 1}`,
    row?.[0],
    row?.[1],
    0,
    0,
    0
  ]);
}

function settingsFromRows(rows) {
  return (rows || []).map((row, index) => [
    parseFloat(row[1]),
    parseFloat(row[2]),
    row[0] || `Range ${index + 1}`
  ]);
}

function getParticleDistributionSourceUrl(csvUrl) {
  if (typeof csvUrl !== 'string') {
    return csvUrl;
  }

  return csvUrl.replace(/indiGrainResultsAppDisplay\.csv(?=$|[?#])/, 'indiGrainResultsCleaned.csv');
}

function calculateParticleDistribution(rows, indiGrainHeader, indiGrainData, colIndex, rejectSnapshot) {
  const dist = (rows || []).map((row) => [row[0], row[1], row[2], 0, 0, 0]);
  if (!Array.isArray(indiGrainData) || indiGrainData.length === 0) {
    return dist;
  }

  const filteredGrains = Array.isArray(indiGrainHeader) && indiGrainHeader.length > 0
    ? filterMeasurementRowsByRejectSnapshot(indiGrainHeader, indiGrainData, rejectSnapshot)
    : filterRowsByRejectSnapshot(indiGrainData, rejectSnapshot, undefined);
  let totalCount = 0;
  let totalVolume = 0;
  filteredGrains.forEach((row) => {
    const metric = parseFloat(row[colIndex]);
    const volume = parseFloat(row[4]);
    if (Number.isNaN(metric)) {
      return;
    }
    totalCount += 1;
    if (!Number.isNaN(volume)) {
      totalVolume += volume;
    }
    for (let i = 0; i < dist.length; i++) {
      const interval = dist[i];
      if (metric >= parseFloat(interval[1]) && metric < parseFloat(interval[2])) {
        interval[3] += 1;
        if (!Number.isNaN(volume)) {
          interval[5] += volume;
        }
      }
    }
  });

  for (let i = 0; i < dist.length; i++) {
    dist[i][4] = totalCount > 0 ? (dist[i][3] / totalCount) * 100 : 0;
    dist[i][5] = totalVolume > 0 ? (dist[i][5] / totalVolume) * 100 : 0;
  }
  return dist;
}

export default function ParticleDistributionTable(props) {
  const { isMobile } = useResponsive();
  const readOnly = props.readOnly === true;
  const mobileHeaderFontSize = 11;
  const mobileCellFontSize = 12;

  const dispatch = useDispatch();
  const toast = useToast();

  const colorMode = useSelector((store) => store.settings.colorMode);
  const locale = useSelector((store) => store.app.locale);
  const isOnline = useSelector((store) => store.app.isOnline);

  const username = useSelector((store) => store.user.userProfile.response?.username);
  const orgId = useSelector((store) => store.user.userProfile.response?.org?.orgId);

  const settingName = `particle_distribution_${props.colIndex}`;
  const settingsKey = getSettingsKey({ orgId, grainId: props.grainId, profileId: props.profileId, settingName });
  const particleDistSettings = useSelector((store) => store.user.userSettings);
  const currentParticleDistSetting = particleDistSettings?.[settingsKey];
  const rejectSelectionSnapshot = props.rejectionCalculationConfig?.particleDistribution;

  const [saveSettingsVisible, setSaveSettingsVisible] = React.useState(false);
  const [updatedSettings, setUpdatedSettings] = React.useState(undefined);
  const [invalidCells, setInvalidCells] = React.useState({});
  const [rawValues, setRawValues] = React.useState({});
  const [localSettingsStatus, setLocalSettingsStatus] = React.useState('idle');

  const [state, setState] = React.useState({
    tableHead: TABLE_HEAD,
    tableData: rowsFromSettings(undefined, settingName),
    indiGrainHeader: [],
    indiGrainData: []
  });

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      setSaveSettingsVisible(false);
      setUpdatedSettings(undefined);
      setInvalidCells({});
      setRawValues({});
      setLocalSettingsStatus('loading');

      if (!username || !props.grainId || !props.profileId) {
        setLocalSettingsStatus('idle');
        return;
      }

      if (isElectron() && typeof window !== 'undefined' && window.electron?.getLocalProfileSetting) {
        const resp = await window.electron.getLocalProfileSetting({
          username,
          orgId,
          grainId: props.grainId,
          profileId: props.profileId,
          settingName
        });
        if (cancelled) {
          return;
        }
        if (resp?.success && resp.value) {
          const rows = rowsFromSettings(resp.value, settingName);
          setLocalSettingsStatus('found');
          dispatch(UserActions.setUserSettingsValue({ key: settingsKey, value: resp.value }));
          setState((prev) => ({
            ...prev,
            tableData: calculateParticleDistribution(rows, prev.indiGrainHeader, prev.indiGrainData, props.colIndex, rejectSelectionSnapshot)
          }));
          return;
        }
        setLocalSettingsStatus('missing');
      } else {
        setLocalSettingsStatus('missing');
      }

      dispatch(UserActions.getUserSettings.request({
        username,
        grainId: props.grainId,
        profileId: props.profileId,
        orgId,
        settingName
      }));
    };

    loadSettings().catch((error) => console.error('Failed to load particle distribution settings', error));

    return () => {
      cancelled = true;
    };
  }, [props.grainId, props.profileId, props.colIndex, username, orgId]);

  useEffect(() => {
    fetchIndiGrainData(props.csvUrl);
  }, [props.csvUrl]);

  useEffect(() => {
    const settings = currentParticleDistSetting;
    if (settings === undefined || settings === null) {
      return;
    }

    const rows = rowsFromSettings(settings, settingName);
    setState((prev) => ({
      ...prev,
      tableData: calculateParticleDistribution(rows, prev.indiGrainHeader, prev.indiGrainData, props.colIndex, rejectSelectionSnapshot)
    }));

    if (
      localSettingsStatus === 'missing' &&
      isElectron() &&
      typeof window !== 'undefined' &&
      window.electron?.upsertLocalProfileSetting &&
      username &&
      props.grainId &&
      props.profileId
    ) {
      window.electron.upsertLocalProfileSetting({
        username,
        orgId,
        grainId: props.grainId,
        profileId: props.profileId,
        settingName,
        value: settings,
        lastSyncedValue: settings,
        syncStatus: 'synced'
      }).catch((error) => console.error('Failed to cache cloud particle settings locally', error));
    }
  }, [currentParticleDistSetting, settingsKey, state.indiGrainHeader, state.indiGrainData, props.colIndex, localSettingsStatus, rejectSelectionSnapshot]);

  useEffect(() => {
    const rows = rowsFromSettings(currentParticleDistSetting, settingName);
    setState((prev) => ({
      ...prev,
      tableData: calculateParticleDistribution(rows, prev.indiGrainHeader, prev.indiGrainData, props.colIndex, rejectSelectionSnapshot)
    }));
  }, [currentParticleDistSetting, state.indiGrainHeader, state.indiGrainData, props.colIndex, rejectSelectionSnapshot, settingName]);

  const validationRules = {
    name: (value, rowIndex) => {
      if (!value || value.trim() === '') {
        return { isValid: false, message: getText('rowNameEmpty', { row: rowIndex + 1 }) };
      }
      return { isValid: true };
    },
    lowerBound: (value, rowIndex, upperBound) => {
      if (value === '' || value === null || value === undefined) {
        return { isValid: false, message: getText('rowLowerBoundEmpty', { row: rowIndex + 1 }) };
      }
      if (isNaN(parseFloat(value))) {
        return { isValid: false, message: getText('rowLowerBoundNumber', { row: rowIndex + 1 }) };
      }
      if (upperBound !== '' && upperBound !== null && upperBound !== undefined && !isNaN(parseFloat(upperBound))) {
        if (parseFloat(value) >= parseFloat(upperBound)) {
          return { isValid: false, message: getText('rowLowerBoundLess', { row: rowIndex + 1 }) };
        }
      }
      return { isValid: true };
    },
    upperBound: (value, rowIndex, lowerBound) => {
      if (value === '' || value === null || value === undefined) {
        return { isValid: false, message: getText('rowUpperBoundEmpty', { row: rowIndex + 1 }) };
      }
      if (isNaN(parseFloat(value))) {
        return { isValid: false, message: getText('rowUpperBoundNumber', { row: rowIndex + 1 }) };
      }
      if (lowerBound !== '' && lowerBound !== null && lowerBound !== undefined && !isNaN(parseFloat(lowerBound))) {
        if (parseFloat(value) <= parseFloat(lowerBound)) {
          return { isValid: false, message: getText('rowUpperBoundGreater', { row: rowIndex + 1 }) };
        }
      }
      return { isValid: true };
    }
  };

  const validateSettings = (settings) => {
    const errors = {};
    const errorMessages = [];

    for (let i = 0; i < settings.length; i++) {
      const name = settings[i][0];
      const lowerBound = settings[i][1];
      const upperBound = settings[i][2];

      const nameValidation = validationRules.name(name, i);
      if (!nameValidation.isValid) {
        errors[`${i}-0`] = true;
        errorMessages.push(nameValidation.message);
      }

      const lowerValidation = validationRules.lowerBound(lowerBound, i, upperBound);
      if (!lowerValidation.isValid) {
        errors[`${i}-1`] = true;
        errorMessages.push(lowerValidation.message);
      }

      const upperValidation = validationRules.upperBound(upperBound, i, lowerBound);
      if (!upperValidation.isValid) {
        errors[`${i}-2`] = true;
        errorMessages.push(upperValidation.message);
      }
    }

    setInvalidCells(errors);
    return { isValid: errorMessages.length === 0, errors: errorMessages };
  };

  const updateParticleDistributionSettings = async () => {
    const rowsToSave = updatedSettings || state.tableData;
    const validation = validateSettings(rowsToSave);
    if (!validation.isValid) {
      const errorMessage = validation.errors.length === 1
        ? validation.errors[0]
        : getText('validationErrors', {
          count: validation.errors.length,
          errors: validation.errors.slice(0, 3).join('\n'),
          suffix: validation.errors.length > 3 ? getText('validationMoreErrors') : ''
        });

      toast.show({
        description: errorMessage,
        status: "error",
        duration: 4000
      });
      return;
    }

    const newParticleDistSetting = settingsFromRows(rowsToSave);

    if (isElectron() && typeof window !== 'undefined' && window.electron?.upsertLocalProfileSetting) {
      const resp = await window.electron.upsertLocalProfileSetting({
        username,
        orgId,
        grainId: props.grainId,
        profileId: props.profileId,
        settingName,
        value: newParticleDistSetting,
        syncStatus: 'pending'
      });
      if (!resp?.success) {
        throw new Error(resp?.error || 'Failed to save particle distribution settings locally');
      }
      dispatch(UserActions.setUserSettingsValue({ key: settingsKey, value: newParticleDistSetting }));

      if (props.jobId && window.electron?.generateParticleDistributionCsvsForJob) {
        const generateResp = await window.electron.generateParticleDistributionCsvsForJob({
          jobId: props.jobId,
          username,
          orgId,
          grainId: props.grainId,
          profileId: props.profileId,
          rejectionCalculationConfig: props.rejectionCalculationConfig
        });
        if (!generateResp?.success) {
          console.warn('Failed to regenerate particle distribution CSVs', generateResp?.error);
        } else if (generateResp?.changed) {
          dispatch(JobActions.pollJobStatus.request(props.jobId));
        }
      }
    } else if (isOnline) {
      dispatch(UserActions.updateUserSettings.request({
        username,
        orgId,
        grainId: props.grainId,
        profileId: props.profileId,
        newSetting: newParticleDistSetting,
        settingName
      }));
      dispatch(UserActions.setUserSettingsValue({ key: settingsKey, value: newParticleDistSetting }));
    }

    setSaveSettingsVisible(false);
    setInvalidCells({});
    setRawValues({});
  }

  const fetchIndiGrainData = async (csvUrl) => {
    if (csvUrl === undefined) {
      return
    }

    try {
      const preferredSourceUrl = getParticleDistributionSourceUrl(csvUrl);
      const candidateUrls = preferredSourceUrl === csvUrl
        ? [csvUrl]
        : [preferredSourceUrl, csvUrl];
      let csvText;
      let lastError;

      for (const sourceUrl of candidateUrls) {
        try {
          if (isElectron() && typeof window !== 'undefined' && window.electron?.fetchFileContent) {
            csvText = await window.electron.fetchFileContent(sourceUrl);
          } else if (sourceUrl.startsWith('file://')) {
            if (!isElectron() || typeof window === 'undefined' || !window.electron?.readLocalFile) {
              throw new Error('Local file access not available');
            }
            csvText = await window.electron.readLocalFile(sourceUrl);
          } else {
            const response = await fetch(sourceUrl);
            if (!response.ok) {
              throw new Error(`Failed to fetch CSV: ${response.status}`);
            }
            csvText = await response.text();
          }
          if (csvText !== undefined) {
            break;
          }
        } catch (error) {
          lastError = error;
        }
      }

      if (csvText === undefined) {
        throw lastError || new Error('Failed to load particle distribution source csv');
      }

      const csvRow = await csv({
        noheader: true,
        output: "csv"
      }).fromString(csvText);

      setState((prev) => ({
        ...prev,
        indiGrainHeader: csvRow[0] || [],
        indiGrainData: csvRow.slice(1)
      }));
    } catch (error) {
      console.error("some error occurred", error);
    }
  }

  const getRowDistOnChange = (rowIndex, colIndex, newVal) => {
    let tData = state.tableData
    let dist = [];
    for (let i = 0; i < tData.length; i++) {
      dist.push([tData[i][0], tData[i][1], tData[i][2]])
    }

    if (colIndex === 1 || colIndex === 2) {
      const validated = newVal.match(/^(\d*\.{0,1}\d{0,2}$)/)
      if (validated) {
        dist[rowIndex][colIndex] = newVal;
      }
    } else {
      dist[rowIndex][colIndex] = newVal;
    }

    for (let i = 0; i < dist.length; i++) {
      dist[i][3] = 0;
      dist[i][4] = 0;
      dist[i][5] = 0;
    }

    return dist;
  }

  const beautify = (data) => {
    if (!isNaN(parseFloat(data)) && !isNaN(data - 0)) {
      let floatData = parseFloat(data);
      if (Math.ceil(floatData) == Math.floor(floatData)) {
        return data;
      } else {
        return floatData.toFixed(2);
      }
    } else {
      return data;
    }
  }

  const element = (data, rowIndex, colIndex) => {
    const cellKey = `${rowIndex}-${colIndex}`;
    const isInvalid = invalidCells[cellKey];

    let displayValue = (colIndex === 1 || colIndex === 2) && rawValues[cellKey] !== undefined
      ? rawValues[cellKey]
      : (colIndex === 0 ? data : beautify(data));
    displayValue = String(displayValue);

    return (
      <Input selectTextOnFocus
        scrollEnabled={false}
        key={`${rowIndex},${colIndex}`}
        value={displayValue}
        style={[
          {
            color: colorMode == "dark" ? "white" : "black",
            fontSize: isMobile ? mobileCellFontSize : 15,
            textAlign: 'center',
            paddingHorizontal: isMobile ? 4 : 8
          },
          isInvalid && { borderColor: 'red', borderWidth: 2 }
        ]}
        keyboardType={colIndex === 1 || colIndex === 2 ? "decimal-pad" : "default"}
        onChangeText={(text) => {
          if (colIndex === 1 || colIndex === 2) {
            setRawValues(prev => ({ ...prev, [cellKey]: text }));
          }

          let rowDist = getRowDistOnChange(rowIndex, colIndex, text);
          const calculated = calculateParticleDistribution(
            rowDist,
            state.indiGrainHeader,
            state.indiGrainData,
            props.colIndex,
            rejectSelectionSnapshot
          );
          setSaveSettingsVisible(true);
          setUpdatedSettings(calculated);
          setState((prev) => ({ ...prev, tableData: calculated }));
        }}
        onBlur={() => {
          if (colIndex === 1 || colIndex === 2) {
            setRawValues(prev => {
              const updated = { ...prev };
              delete updated[cellKey];
              return updated;
            });
          }
        }} />
    );
  };

  const textElement = (data) => (
    <Text style={[tableStyle.cellText, {
      color: colorMode === 'dark' ? 'white' : 'black',
      fontSize: isMobile ? mobileCellFontSize : 15,
      marginHorizontal: isMobile ? 6 : 10,
      marginVertical: isMobile ? 8 : 10,
      textAlign: 'center'
    }]}>{String(data)}</Text>
  );

  const getHeaderLabel = (label) => {
    if (!isMobile) {
      return localizeResultLabel(label, { locale });
    }

    switch (label) {
      case 'Lower Bound':
        return getText('particleDistLowRangeShort');
      case 'Upper Bound':
        return getText('particleDistUpRangeShort');
      case '% by count':
        return getText('particleDistPercentCountShort');
      case '% by weight':
        return getText('particleDistPercentWeightShort');
      default:
        return localizeResultLabel(label, { locale });
    }
  };

  return (
    <View style={tableStyle.tableContainer}>
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
      }}>
        <Text style={textStyle.tableTitleText}>{props.title}</Text>
        {!readOnly && saveSettingsVisible && <Button variant={"link"} onPress={async () => {
          await updateParticleDistributionSettings()
        }}>{getText('saveSettings')}</Button>}
      </View>
      <Table borderStyle={tableStyle.border}>
        <TableWrapper style={[tableStyle.head, isMobile && { height: 48 }]}>
          {
            state.tableHead.map((cellData, index) => (
              <Cell
                key={index}
                data={getHeaderLabel(cellData)}
                textStyle={[
                  tableStyle.cellText,
                  {
                    color: colorMode === 'dark' ? 'white' : 'black',
                    textAlign: 'center',
                    fontSize: isMobile ? mobileHeaderFontSize : 15,
                    fontWeight: '600',
                    marginHorizontal: isMobile ? 4 : 10,
                    marginVertical: isMobile ? 6 : 10,
                  }
                ]}
              />
            ))
          }
        </TableWrapper>
        {
          state.tableData.map((rowData, index) => (
            <TableWrapper key={index} style={[tableStyle.row, isMobile && { minHeight: 42 }]}>
              {
                rowData.map((cellData, cellIndex) => (
                  <Cell key={cellIndex} data={!readOnly && (cellIndex === 0
                    || cellIndex === 1
                    || cellIndex === 2) ? element(cellData, index, cellIndex)
                    : textElement(beautify(cellData))} textStyle={tableStyle.cellText} />
                ))
              }
            </TableWrapper>
          ))
        }
      </Table>
    </View>
  );
};
